/** GPU Box Blur — separable 2-pass fragment-shader port of
 *  `lib/filters/box-blur.ts`.
 *
 *  Same separable structure as Gaussian: horizontal then vertical
 *  pass, each a 1D convolution along its axis. Box-blur weights
 *  are uniform `1 / (2r + 1)` per tap, so the shader avoids a
 *  kernel array entirely — just sums the taps and divides by the
 *  count.
 *
 *  The JS path uses a sliding-window running-sum optimization to
 *  collapse the per-pixel kernel summation to O(1). On the GPU
 *  the trick isn't needed: each output pixel runs in parallel,
 *  and even O(r) sample work at r=200 is fast on hardware. Direct
 *  summation keeps the shader straightforward and parallelism-
 *  friendly.
 *
 *  Identity (radius=0) short-circuits to a canvas2D copy via the
 *  shared `applyIdentityCopy` helper, matching the JS path. */

import type { BoxBlurValue } from '../../filters/box-blur';
import { MAX_KERNEL_RADIUS } from '../blur-shared';
import { applyIdentityCopy } from '../identity-copy';
import { applyShaderPasses } from '../runtime';

const HORIZONTAL_FRAG = `#version 300 es
precision highp float;
in vec2 v_uv;
uniform sampler2D u_source;
uniform vec2 u_dims;
uniform int u_radius;
out vec4 outColor;

void main() {
  float pxX = v_uv.x * u_dims.x - 0.5;
  float pxY = v_uv.y * u_dims.y - 0.5;
  vec3 sum = vec3(0.0);
  vec4 center = texture(u_source, v_uv);
  for (int k = -${MAX_KERNEL_RADIUS}; k <= ${MAX_KERNEL_RADIUS}; k++) {
    if (k < -u_radius || k > u_radius) continue;
    float sxClamped = clamp(pxX + float(k), 0.0, u_dims.x - 1.0);
    vec2 uv = vec2((sxClamped + 0.5) / u_dims.x, (pxY + 0.5) / u_dims.y);
    sum += texture(u_source, uv).rgb;
  }
  float denom = float(2 * u_radius + 1);
  outColor = vec4(sum / denom, center.a);
}
`;

const VERTICAL_FRAG = `#version 300 es
precision highp float;
in vec2 v_uv;
uniform sampler2D u_source;
uniform vec2 u_dims;
uniform int u_radius;
out vec4 outColor;

void main() {
  float pxX = v_uv.x * u_dims.x - 0.5;
  float pxY = v_uv.y * u_dims.y - 0.5;
  vec3 sum = vec3(0.0);
  vec4 center = texture(u_source, v_uv);
  for (int k = -${MAX_KERNEL_RADIUS}; k <= ${MAX_KERNEL_RADIUS}; k++) {
    if (k < -u_radius || k > u_radius) continue;
    float syClamped = clamp(pxY + float(k), 0.0, u_dims.y - 1.0);
    vec2 uv = vec2((pxX + 0.5) / u_dims.x, (syClamped + 0.5) / u_dims.y);
    sum += texture(u_source, uv).rgb;
  }
  float denom = float(2 * u_radius + 1);
  outColor = vec4(sum / denom, center.a);
}
`;

export function applyBoxBlurOnGpu(
  source: ImageData,
  target: HTMLCanvasElement,
  value: BoxBlurValue,
): boolean {
  const radius = Math.max(0, Math.floor(value.radius));
  if (radius === 0) return applyIdentityCopy(source, target);
  if (radius > MAX_KERNEL_RADIUS) return false;
  const dims: [number, number] = [source.width, source.height];
  return applyShaderPasses({
    source,
    target,
    passes: [
      {
        fragShaderSrc: HORIZONTAL_FRAG,
        uniforms: {
          u_dims: { type: 'vec2', value: dims },
          u_radius: { type: 'int', value: radius },
        },
      },
      {
        fragShaderSrc: VERTICAL_FRAG,
        uniforms: {
          u_dims: { type: 'vec2', value: dims },
          u_radius: { type: 'int', value: radius },
        },
      },
    ],
  });
}
